import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Modal, ModalProps } from '@makyoapp/makyo-ui'

import './LoadingGenerateNewAppModal.css'

interface LoadingGenerateNewAppModalProps {
  show: ModalProps['show']
  toggleVisibility: ModalProps['onHide']
}

export default function LoadingGenerateNewAppModal({ show, toggleVisibility }: LoadingGenerateNewAppModalProps) {
  const { t } = useTranslation()
  const [completedSteps, setCompletedSteps] = useState<number>(0)

  const extractingFillRef = useRef<HTMLDivElement>(null)
  const extractingPercentageRef = useRef<HTMLSpanElement>(null)
  const databaseFillRef = useRef<HTMLDivElement>(null)
  const databasePercentageRef = useRef<HTMLSpanElement>(null)
  const screensFillRef = useRef<HTMLDivElement>(null)
  const screensPercentageRef = useRef<HTMLSpanElement>(null)
  const componentsFillRef = useRef<HTMLDivElement>(null)
  const componentsPercentageRef = useRef<HTMLSpanElement>(null)

  const progressRefs = useMemo(
    () => ({
      extracting: {
        fill: extractingFillRef,
        percentage: extractingPercentageRef,
      },
      database: {
        fill: databaseFillRef,
        percentage: databasePercentageRef,
      },
      screens: {
        fill: screensFillRef,
        percentage: screensPercentageRef,
      },
      components: {
        fill: componentsFillRef,
        percentage: componentsPercentageRef,
      },
    }),
    []
  )

  const updateProgress = (type: keyof typeof progressRefs, percentage: number) => {
    const { fill, percentage: percentageRef } = progressRefs[type]
    if (fill.current && percentageRef.current) {
      fill.current.style.width = `${percentage}%`
      percentageRef.current.textContent = `${percentage}%`
    }

    if (percentage >= 100) {
      setCompletedSteps((prev) => prev + 1)
    }
  }

  const animateProgress = async (type: keyof typeof progressRefs, duration: number): Promise<void> => {
    return new Promise((resolve) => {
      let progress = 0
      const interval = setInterval(() => {
        progress += 1
        updateProgress(type, progress)
        if (progress === 100) {
          clearInterval(interval)
          resolve()
        }
      }, duration / 100)
    })
  }

  useEffect(() => {
    const runAnimations = async () => {
      await Promise.all([
        animateProgress('extracting', 3000),
        animateProgress('database', 4000),
        animateProgress('screens', 5000),
        animateProgress('components', 6000),
      ])
    }
    runAnimations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal id='org-detail-generate-new-app-modal' show={show} onHide={toggleVisibility}>
      <Modal.Header onCloseClick={toggleVisibility}>{t('Creating Your App')}</Modal.Header>
      <Modal.Body className='modal-md'>
        {Object.keys(progressRefs).map((key) => (
          <div key={key} className='progress-container'>
            <div className='progress-label'>
              <span>{t(`Creating ${key}`)}</span>
              <span ref={progressRefs[key as keyof typeof progressRefs].percentage} id={`${key}-percentage`}>
                0%
              </span>
            </div>
            <div className='progress-bar'>
              <div
                ref={progressRefs[key as keyof typeof progressRefs].fill}
                id={`${key}-fill`}
                className='progress-fill'
              />
            </div>
          </div>
        ))}
        <button
          id='view-app-button'
          className={`view-app-button ${completedSteps >= 4 ? 'active' : ''}`}
          disabled={completedSteps < 4}
        >
          {t('View App')}
        </button>
      </Modal.Body>
    </Modal>
  )
}
